import type { ClientInvoice } from "@/features/finances/types";

export interface PrzFilterLabel {
  label: string;
  value: string;
}

export interface PrzPrintContext {
  invoices: ClientInvoice[];
  filterLabels: PrzFilterLabel[];
  generatedAt: string;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}.${m}.${y}`;
}

function fmtAmt(val: string | number | undefined | null): string {
  if (val == null || val === "") return "—";
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n)) return "—";
  return n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Wrap cell text in a div that clips overflow — the only reliable cross-browser method.
// overflow:hidden on <td> itself is not respected by most browsers.
function cell(text: string, extra = ""): string {
  return `<div class="c"${extra}>${text}</div>`;
}

function cellR(text: string): string {
  return `<div class="c r">${text}</div>`;
}

function invStatus(inv: ClientInvoice): string {
  if (inv.status === "issued") return "Wystawiona";
  if (inv.director_approved === true) return "Szkic (zat.)";
  return "Szkic";
}

function invType(inv: ClientInvoice): string {
  if (inv.invoice_type === "proforma_advance") return "Proforma zal.";
  if (inv.invoice_type === "proforma") return "Proforma";
  return "FV";
}

function payStatus(inv: ClientInvoice): string {
  if (inv.payment_status === "paid") return "Opłacona";
  if (inv.payment_status === "partially_paid") return "Częściowo";
  return "Nieopłacona";
}

function rowBg(inv: ClientInvoice): string {
  const today = new Date().toISOString().slice(0, 10);
  if (inv.payment_status === "paid") return "#dcfce7";
  if (inv.payment_status === "partially_paid") return "#f0fdf4";
  if (inv.status === "issued" && inv.payment_due_date && inv.payment_due_date < today)
    return "#fef2f2";
  return "";
}

export function printPrzInvoicesPDF(ctx: PrzPrintContext): void {
  const { invoices, filterLabels, generatedAt } = ctx;

  let tGross = 0, tNet = 0, tPaid = 0, tRem = 0;
  for (const inv of invoices) {
    const g = parseFloat(inv.amount_gross) || 0;
    const n = parseFloat(inv.amount_net) || 0;
    const pg = parseFloat(inv.paid_amount || "0") || 0;
    const pn = g > 0 ? (pg * n) / g : 0;
    tGross += g; tNet += n; tPaid += pg; tRem += Math.max(0, n - pn);
  }

  const chips = filterLabels.length
    ? filterLabels.map(f => `<span class="chip"><b>${esc(f.label)}:</b> ${esc(f.value)}</span>`).join(" ")
    : `<i style="color:#9ca3af">Brak aktywnych filtrów.</i>`;

  const rows = invoices.map(inv => {
    const g = parseFloat(inv.amount_gross) || 0;
    const n = parseFloat(inv.amount_net) || 0;
    const pg = parseFloat(inv.paid_amount || "0") || 0;
    const pn = g > 0 ? (pg * n) / g : 0;
    const rem = Math.max(0, n - pn);
    const bg = rowBg(inv);

    return `<tr${bg ? ` style="background:${bg}"` : ""}>
      <td>${cell(esc(inv.invoice_number || "—"))}</td>
      <td>${cell(fmtDate(inv.invoice_date))}</td>
      <td>${cell(esc(inv.company_name ?? "—"))}</td>
      <td>${cell(esc(inv.client_name || inv.project_name || "—"))}</td>
      <td>${cell(invType(inv))}</td>
      <td>${cell(invStatus(inv))}</td>
      <td>${cell(payStatus(inv))}</td>
      <td>${cellR(fmtAmt(inv.amount_gross))}</td>
      <td>${cellR(fmtAmt(inv.amount_net))}</td>
      <td>${cellR(fmtAmt(inv.paid_amount))}</td>
      <td>${cellR(rem > 0.005 ? `<span style="color:#b91c1c">${fmtAmt(rem.toFixed(2))}</span>` : "—")}</td>
      <td>${cell(fmtDate(inv.payment_due_date))}</td>
      <td>${cell(esc(inv.description || ""))}</td>
    </tr>`;
  }).join("");

  const plural = invoices.length === 1 ? "faktura" : invoices.length < 5 ? "faktury" : "faktur";

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
<meta charset="UTF-8"/>
<title>Faktury PRZ — GK ELEKTIM</title>
<style>
/* ─── Print: A4 landscape, 14/16 mm margins ──────────────────── */
@page { size: A4 landscape; margin: 14mm 16mm; }

* { box-sizing: border-box; margin: 0; padding: 0; }

/* Grey screen background so the white page is clearly delimited */
html { background: #9ca3af; }
body { background: #9ca3af; padding: 14mm; }
@media print { html, body { background: white; padding: 0; } }

/* ─── White page card ─────────────────────────────────────────── */
/* 265mm = 297 - 2×16 mm printable width @ 96dpi = 1003px        */
.page {
  width: 265mm;
  background: white;
  margin: 0 auto;
  padding: 0;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 7pt;
  color: #111;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
@media print { .page { width: auto; } }

/* ─── Header ──────────────────────────────────────────────────── */
.hdr {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1.5pt solid #1e40af;
  padding-bottom: 4pt;
  margin-bottom: 5pt;
}
.htitle { font-size: 10.5pt; font-weight: bold; color: #1e40af; }
.hsub   { font-size: 6.5pt; color: #6b7280; margin-top: 2pt; }
.hmeta  { font-size: 6.5pt; color: #6b7280; text-align: right; line-height: 1.7; }

/* ─── Filter bar ──────────────────────────────────────────────── */
.fbar {
  background: #f8fafc;
  border: 0.5pt solid #e2e8f0;
  border-radius: 2pt;
  padding: 3pt 5pt;
  margin-bottom: 5pt;
  font-size: 6.5pt;
  line-height: 2;
}
.fbar::before { content: "Kryteria filtra: "; font-weight: bold; color: #374151; }
.chip {
  display: inline-block;
  background: white;
  border: 0.5pt solid #cbd5e1;
  border-radius: 2pt;
  padding: 1pt 4pt;
  margin: 1pt 2pt;
}

/* ─── Table ───────────────────────────────────────────────────── */
table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 6.5pt;
}
/* Widths on <col> directly in HTML attributes */

thead tr { background: #1e40af; color: #fff; }
thead th { padding: 0; font-size: 6pt; font-weight: bold; }
/* Header inner div — same clip mechanism as data cells */
thead th .c { padding: 3px 3px; }

tbody tr   { border-bottom: 0.4pt solid #e5e7eb; }
tbody tr:nth-child(even):not([style]) { background: #f9fafb; }

/* td has NO overflow — the inner div handles it */
td { padding: 0; vertical-align: middle; }

/* ─── Inner cell div — THE key: overflow clipping ────────────── */
/* width:100% + display:block are required — without an explicit width
   the div has no reference point and overflow:hidden does nothing.   */
.c {
  display: block;
  width: 100%;
  padding: 2px 3px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.c.r { text-align: right; font-family: "Courier New", monospace; }

tfoot tr { border-top: 1pt solid #374151; }
tfoot td { padding: 0; background: #f1f5f9; }
tfoot td .c { padding: 3px 3px; font-weight: bold; font-size: 6.5pt; }

/* ─── Legend ──────────────────────────────────────────────────── */
.legend { margin-top: 4pt; font-size: 6pt; color: #6b7280; display: flex; gap: 10pt; }
.sw {
  display: inline-block; width: 7pt; height: 7pt;
  border: 0.5pt solid #ccc; border-radius: 1pt;
  vertical-align: middle; margin-right: 2pt;
}
</style>
</head>
<body>
<div class="page">

<div class="hdr">
  <div>
    <div class="htitle">GK ELEKTIM — Faktury PRZ</div>
    <div class="hsub">Zestawienie faktur wystawionych przez GK ELEKTIM do Klienta/Inwestora</div>
  </div>
  <div class="hmeta">Wygenerowano: ${generatedAt}<br/>Liczba rekordów: <b>${invoices.length}</b></div>
</div>

<div class="fbar">${chips}</div>

<table>
  <colgroup>
    <col style="width:9%"/><col style="width:6%"/><col style="width:8%"/><col style="width:11%"/>
    <col style="width:6%"/><col style="width:7%"/><col style="width:7%"/>
    <col style="width:7%"/><col style="width:7%"/><col style="width:7%"/><col style="width:7%"/>
    <col style="width:6%"/><col style="width:12%"/>
  </colgroup>
  <thead>
    <tr>
      <th><div class="c">Nr FV</div></th>
      <th><div class="c">Data wyst.</div></th>
      <th><div class="c">Spółka</div></th>
      <th><div class="c">Nabywca / Projekt</div></th>
      <th><div class="c">Typ</div></th>
      <th><div class="c">Status FV</div></th>
      <th><div class="c">Płatność</div></th>
      <th><div class="c r">Brutto (PLN)</div></th>
      <th><div class="c r">Netto (PLN)</div></th>
      <th><div class="c r">Zapł. brutto</div></th>
      <th><div class="c r">Pozostaje</div></th>
      <th><div class="c">Termin płatn.</div></th>
      <th><div class="c">Opis</div></th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr>
      <td colspan="7"><div class="c">RAZEM (${invoices.length} ${plural})</div></td>
      <td><div class="c r">${fmtAmt(tGross.toFixed(2))}</div></td>
      <td><div class="c r">${fmtAmt(tNet.toFixed(2))}</div></td>
      <td><div class="c r">${fmtAmt(tPaid.toFixed(2))}</div></td>
      <td><div class="c r" style="color:#b91c1c">${fmtAmt(tRem.toFixed(2))}</div></td>
      <td><div class="c"></div></td>
      <td><div class="c"></div></td>
    </tr>
  </tfoot>
</table>

<div class="legend">
  <span><span class="sw" style="background:#dcfce7"></span>Opłacona</span>
  <span><span class="sw" style="background:#f0fdf4"></span>Opłacona częściowo</span>
  <span><span class="sw" style="background:#fef2f2"></span>Po terminie płatności</span>
</div>

</div>
<script>window.onload=function(){window.print();};</script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=1200,height=900");
  if (w) w.addEventListener("afterprint", () => URL.revokeObjectURL(url));
  else URL.revokeObjectURL(url);
}
